import React, { useMemo } from "react"
import { motion, useReducedMotion } from "motion/react"
import {
	IconArrowUpRightOutline18 as ArrowUpRight,
	IconCheckOutline18 as Check,
	IconScrollTextOutline18 as ScrollText,
	IconCircleXmarkOutline18 as XCircle,
} from "nucleo-ui-outline-18"

import type { AgentSetupActionStatus } from "@productlane/lib/src/ai"
import { compactLineDiff, lineDiff } from "@productlane/lib/src/ai/line-diff"
import { cn } from "@productlane/lib/src/cn"

import { LinearIcon } from "../../icons/linear-logo"
import { Button } from "../button"

const CARD_ENTER = { duration: 0.28, ease: [0.23, 1, 0.32, 1] as const }

const CardShell = ({
	label,
	icon: Icon,
	children,
}: {
	label: string
	icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
	children: React.ReactNode
}) => {
	const reduce = useReducedMotion()
	return (
		<motion.div
			initial={reduce ? { opacity: 0 } : { opacity: 0, y: 6 }}
			animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
			transition={CARD_ENTER}
			className="my-3 flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-900"
		>
			<span className="flex items-center gap-1.5 px-4 pt-3.5 text-[10px] font-medium tracking-[0.08em] text-tertiary">
				<Icon className="h-3.5 w-3.5 flex-none" />
				{label}
			</span>
			{children}
		</motion.div>
	)
}

// One footer for both cards: the buttons keep their box while the action runs,
// and every settled state renders in the same row height.
const ApprovalFooter = ({
	status,
	approveLabel,
	disabled,
	onApprove,
	onReject,
	executed,
}: {
	status: AgentSetupActionStatus
	approveLabel: string
	disabled: boolean
	onApprove: () => void
	onReject: () => void
	executed: React.ReactNode
}) => (
	<div className="flex min-h-12 items-center justify-end gap-2 border-t border-gray-200 bg-gray-50/50 px-4 py-2.5 dark:border-gray-700/80 dark:bg-gray-900/40">
		{(status === "pending" || status === "executing") && (
			<>
				<Button
					variant="outline"
					size="sm"
					className="h-7 rounded-md text-xs font-medium"
					disabled={disabled || status === "executing"}
					onClick={onReject}
				>
					Reject
				</Button>
				<Button
					size="sm"
					className="h-7 rounded-md text-xs font-medium"
					disabled={disabled || status === "executing"}
					isLoading={status === "executing"}
					onClick={onApprove}
				>
					{approveLabel}
				</Button>
			</>
		)}
		{status === "executed" && executed}
		{status === "rejected" && (
			<span className="flex items-center gap-1 text-[11px] text-tertiary">
				<XCircle className="h-3.5 w-3.5 flex-none" />
				Rejected
			</span>
		)}
		{status === "errored" && (
			<Button
				variant="outline"
				size="sm"
				className="h-7 rounded-md text-xs font-medium"
				disabled={disabled}
				onClick={onApprove}
			>
				Try again
			</Button>
		)}
	</div>
)

const ErrorRow = ({ message }: { message?: string }) =>
	message ? (
		<p className="border-t border-gray-200 px-4 py-2.5 text-[11px] leading-relaxed text-red-500 dark:border-gray-700/80 dark:text-red-400">
			{message}
		</p>
	) : null

export interface LinearIssueProposalCardProps {
	title: string
	description: string
	teamName?: string | null
	status: AgentSetupActionStatus
	created?: { identifier: string; url: string }
	errorMessage?: string
	disabled?: boolean
	onCreate: () => void
	onReject: () => void
}

export const LinearIssueProposalCard = ({
	title,
	description,
	teamName,
	status,
	created,
	errorMessage,
	disabled = false,
	onCreate,
	onReject,
}: LinearIssueProposalCardProps) => (
	<CardShell
		label={teamName ? `Linear issue for ${teamName}` : "Linear issue"}
		icon={LinearIcon}
	>
		<div className="flex flex-col gap-1.5 px-4 pb-3 pt-1.5">
			<p className="text-[13px] font-medium leading-snug text-primary">
				{title}
			</p>
			<p className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words pb-1 text-[11.5px] leading-relaxed text-secondary">
				{description}
			</p>
		</div>
		{status === "errored" && <ErrorRow message={errorMessage} />}
		<ApprovalFooter
			status={status}
			approveLabel="Create issue"
			disabled={disabled}
			onApprove={onCreate}
			onReject={onReject}
			executed={
				<span className="flex items-center gap-2 text-[11px]">
					<span className="flex items-center gap-1 text-green-600 dark:text-green-400">
						<Check className="h-3.5 w-3.5 flex-none" />
						Created
					</span>
					{created && (
						<a
							href={created.url}
							target="_blank"
							rel="noreferrer"
							className="flex items-center gap-0.5 font-medium text-secondary hover:text-primary"
						>
							{created.identifier}
							<ArrowUpRight className="h-3.5 w-3.5 flex-none" />
						</a>
					)}
				</span>
			}
		/>
	</CardShell>
)

export interface AgentInstructionsProposalCardProps {
	before: string | null
	after: string
	reason: string
	status: AgentSetupActionStatus
	errorMessage?: string
	disabled?: boolean
	onSave: () => void
	onReject: () => void
}

export const AgentInstructionsProposalCard = ({
	before,
	after,
	reason,
	status,
	errorMessage,
	disabled = false,
	onSave,
	onReject,
}: AgentInstructionsProposalCardProps) => {
	const diff = useMemo(
		() => compactLineDiff(lineDiff(before ?? "", after)),
		[before, after],
	)

	return (
		<CardShell label="Agent instructions" icon={ScrollText}>
			<p className="px-4 pb-3 pt-1.5 text-[13px] leading-snug text-primary">
				{reason}
			</p>
			<div
				data-testid="instructions-diff"
				className="max-h-56 overflow-y-auto border-t border-gray-200 bg-gray-50/60 py-2 pb-3 font-mono text-[11px] leading-relaxed dark:border-gray-700/80 dark:bg-gray-950/40"
			>
				{diff.map((part, i) =>
					part.kind === "skip" ? (
						<div key={i} className="px-4 text-tertiary">
							{part.count} unchanged line{part.count === 1 ? "" : "s"}
						</div>
					) : (
						<div
							key={i}
							data-kind={part.kind}
							className={cn(
								"whitespace-pre-wrap break-words px-4",
								part.kind === "same" && "text-tertiary",
								part.kind === "added" &&
									"bg-green-500/10 text-green-700 dark:text-green-300",
								part.kind === "removed" &&
									"bg-red-500/10 text-red-700 line-through dark:text-red-300",
							)}
						>
							{part.kind === "added"
								? "+ "
								: part.kind === "removed"
									? "- "
									: "  "}
							{part.text || " "}
						</div>
					),
				)}
			</div>
			{status === "errored" && <ErrorRow message={errorMessage} />}
			<ApprovalFooter
				status={status}
				approveLabel="Save instructions"
				disabled={disabled}
				onApprove={onSave}
				onReject={onReject}
				executed={
					<span className="flex items-center gap-1 text-[11px] text-green-600 dark:text-green-400">
						<Check className="h-3.5 w-3.5 flex-none" />
						Saved
					</span>
				}
			/>
		</CardShell>
	)
}
